import { FiltersState } from '../../../../states/chat-list';
import {
  AssignedSocketUpdates,
  ChatStatuses,
  ChatsUpdates,
  ConversationTag,
  EligibleMessageTypes,
  GroupLevelAccess,
} from '../../../../typings';
import { ChatUnreadCountBroadcast, Status } from '../../../../typings/chat';
import { WAGroupStatus } from '../../../../../../wa-groups/typings/groups';
import {
  BaseSenderBotUser,
  Message,
  MessageMetadata,
  MessageTypes,
  MessageTypesEnum,
  NoteMessage,
  SenderProvider,
  TemplateData,
} from '../../../../typings/message-types';
import { CallSummaryProgressState, CallSummaryProgressStatus } from '../../../../typings/ai-call-summary';

export enum ChatProviderType {
  WHATSAPP = 'WHATSAPP',
  INSTAGRAM = 'INSTAGRAM',
}

export type BaseChatRowData = {
  id: string;
  unreadCount: number;
  assignedUserId: string | null;
  assignedUserName?: string | null;
  chatType: ChatType;
  chatTypeId: string;
  name: string;
  imageUrl: string;
  canManageGroupAccess?: boolean;
  groupAccessLevel?: GroupLevelAccess;
  lastMessage?: LastMessage;
  lastMessageTime?: number;
  tags: ConversationTag[];
  selected?: boolean;
  senderUser?: {
    id: string;
    name: string;
    imageUrl: string;
    provider?: SenderProvider;
    senderBot?: BaseSenderBotUser;
  };
  isDone?: boolean;
  integrationId: string;
  integrationWabaNumber: string;
  integrationWabaPhoneName: string;
  status?: Status;
  integrationDisplayName: string;
  wabaProfilePictureUrl?: string | null;
  isSlaActive: boolean;
  slaDueDate?: string;
  slaBreachLevel?: number;
  eligibleMessageTypes?: Array<EligibleMessageTypes>;
  assigneeUpdatedTime?: number;
  groupChatId?: string;
  errorMessage?: string;
  erroredCount?: number;
  isReminderTriggered?: boolean;
  reminderTime?: number;
  latestReminderTriggeredTime?: string;
  providerType?: ChatProviderType | null;
};

export interface WAGroupChatRowData extends BaseChatRowData {
  chatType: ChatType.WA_GROUP;
  memberCount: number;
  groupChatDescription: string | null;
  clientCustomGroupId?: string | null;
  createdBy: {
    id: string;
    name: string;
  };
  waGroupStatus?: WAGroupStatus;
  inviteLink?: string;
  dateCreated?: number;
}

export interface GroupChatRowData extends BaseChatRowData {
  chatType: ChatType.GROUP;
  memberCount?: number;
  showMemberCount?: boolean;
}

export interface IndividualChatRowData extends BaseChatRowData {
  chatType: ChatType.INDIVIDUAL;
  phoneNumber: string;
  // Additional fields specific to individual chats can be added here
}
export type ChatRowData = WAGroupChatRowData | GroupChatRowData | IndividualChatRowData;

export type SearchChat = Omit<ChatRowData, 'senderUser'>;

export type SearchChatMessage = {
  id: string;
  assignedUserId: string | null;
  assignedUserName: string | null;
  chatType: ChatType;
  chatTypeId: string;
  name: string | null;
  phoneNumber: string;
  imageUrl: string;
  chatMessageId: string;
  message: string;
  messageOriginType: MessageOrigin;
  messageTime: string;
  messageType: MessageTypesEnum;
  integrationWabaNumber: string;
  integrationId: string;
  integrationWabaPhoneName: string;
  integrationDisplayName: string;
  isDone: boolean;
  tags: ConversationTag[];
  readCount?: number;
  deliveryCount?: number;
  erroredCount?: number;
  senderUserName?: string | null;
  memberCount?: number;
  providerType?: ChatProviderType | null;
};

export interface SearchChatPagination {
  hasMoreChats?: boolean;
  nextChatId?: string;
  nextChatTimestamp?: number;
  messageExists?: boolean;
  nextMessageId?: string;
  nextMessageScore?: number;
  nextMessageTimestamp?: number;
}

export interface GetChatSearch {
  chats: Array<SearchChat>;
  chatMessages: Array<SearchChatMessage>;
  paginationOptions: SearchChatPagination;
}

export interface GetChatStatusFilterCount {
  type: keyof Pick<typeof FiltersState, 'ALL_CHATS' | 'MY_CHATS' | 'UNASSIGNED' | 'SLA_STATUS'>;
  label: string;
  counts: {
    type: ChatStatuses;
    label: string;
    count?: string;
  }[];
}

export interface GetChatStatusFilterCountOld {
  type: keyof Pick<typeof FiltersState, 'ALL_CHATS' | 'MY_CHATS' | 'UNASSIGNED' | 'SLA_STATUS'>;
  label: string;
  counts: {
    type: ChatStatuses;
    label: string;
    count?: number;
  }[];
}

export interface GetChatStatusFilterCounts {
  filterCounts: GetChatStatusFilterCount[];
}

export type SelectedChat = Omit<ChatRowData, 'lastMessage'>;

export interface LastMessage {
  id: string;
  message: MessageTypes;
  templateId?: string | null;
  senderId: string;
  messageOriginType?: MessageOrigin | string;
  readCount?: number;
  repliesCount?: number;
  deliveryCount?: number;
  erroredCount?: number;
  messageTime: number;
  unreadCount?: number;
  noResponseCount?: number;
}

export enum MessageOrigin {
  CUSTOMER = 'CUSTOMER',
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export interface TextMessageData {
  text: string;
  messageType: MessageTypesEnum.TEXT | MessageTypesEnum.SYSTEM | MessageTypesEnum.SYSTEM_LOG;
}

export interface TemplateMessageData {
  messageType: MessageTypesEnum.TEMPLATE;
  templateMessage: TemplateData;
}

export interface InteractiveMessageData {
  messageType: MessageTypesEnum.INTERACTIVE;
  interactiveMessage: {
    body: {
      text: string;
    };
    type: 'button';
    footer?: {
      text: string;
    };
    header?: {
      text: string;
      type: 'text';
    };
    buttons: Array<{
      id: string;
      title: string;
    }>;
  };
}

export interface ButtonMessageData {
  text: string;
  payload: string;
  messageType: MessageTypesEnum.BUTTON;
}

export enum ChatType {
  WA_GROUP = 'WA_GROUP',
  GROUP = 'GROUP',
  INDIVIDUAL = 'INDIVIDUAL',
}

export enum UpdateChatRowReducerActionTypes {
  UPDATE = 'update',
  MARK_DONE = 'MARK_DONE',
  MARK_READ = 'MARK_READ',
}

export interface NewMessageChatRow {
  chat: ChatRowData;
  chatId: string;
  message: MessageTypes;
  messageId: string;
  messageTime: number;
  orgId: string;
  senderId: string;
  senderType: MessageOrigin;
  replyMessage: Message | null;
  status: Status;
  statusTimestamp: number;
  messageMetadata: MessageMetadata;
  messageOriginType: MessageOrigin;
  integrationId: string;
  integrationWabaNumber: string;
  integrationWabaPhoneName: string;
  integrationDisplayName: string;
  validUserIds?: string[];
  validUsersCustomerName?: {
    name: string;
    userId: string;
  }[];
  isReminderTriggered?: boolean;
  reminderId?: string;
  reminderTime?: number;
  lastMessageTime?: number;
  lastMessage?: MessageTypes;
  upcomingScheduledChatMessageTime?: string | null;
  upcomingScheduledChatMessageTimezone?: string | null;
}

export type UpdateChatRowsReducerArgs =
  | {
      data: NewMessageChatRow;
      type: UpdateChatRowReducerActionTypes.UPDATE;
    }
  | {
      data: { id: string; isDone?: boolean };
      type: UpdateChatRowReducerActionTypes.MARK_DONE;
    }
  | {
      data: { id: string };
      type: UpdateChatRowReducerActionTypes.MARK_READ;
    };

export enum ChatFiltersEventReasonTypes {
  ASSIGNEE_UPDATES = 'assignee-updates',
  ASSIGNEE_UPDATES_BULK = 'assignee-updates-bulk',
  CHAT_UPDATES = 'chats-updates',
  MARK_READ = 'mark-read',
  IS_DONE = 'is-done',
  CHAT_SLA_STATUS = 'chat-sla-status',
  ASSIGN_TAGS = 'assign-tags',
  NEW_MESSAGE = 'new-message',
  WAITING_FOR_RESPONSE = 'waiting-for-response',
}

export interface WaitingForResponsePayload {
  eventReason: ChatFiltersEventReasonTypes.WAITING_FOR_RESPONSE;
  eventPayload: NewMessageChatRow;
}

export interface NewMessagePayload {
  eventReason: ChatFiltersEventReasonTypes.NEW_MESSAGE;
  eventPayload: NewMessageChatRow;
}

export interface MarkReadPayload {
  eventReason: ChatFiltersEventReasonTypes.MARK_READ;
  eventPayload: ChatUnreadCountBroadcast;
}

export interface IsDonePayload {
  eventReason: ChatFiltersEventReasonTypes.IS_DONE;
  eventPayload: {
    chatId: string;
    isDone: boolean;
    validUserIds?: string[];
  };
}

export interface ChatSlaStatusPayload {
  eventReason: ChatFiltersEventReasonTypes.CHAT_SLA_STATUS;
  eventPayload: {
    chatId: string;
    isSlaActive?: boolean;
    slaDueDate?: string;
    slaBreachLevel?: number;
    validUserIds?: string[];
  };
}

export interface AssignTagsPayload {
  eventReason: ChatFiltersEventReasonTypes.ASSIGN_TAGS;
  eventPayload: {
    chatId: string;
    tags: ConversationTag[];
    validUserIds?: string[];
  };
}

export interface AssigneeUpdatesPayload {
  eventReason: ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES;
  eventPayload: AssignedSocketUpdates;
}

export interface AssigneeUpdatesBulkPayload {
  eventReason: ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES_BULK;
  eventPayload: AssignedSocketUpdates[];
}

export interface ChatsUpdatesPayload {
  eventReason: ChatFiltersEventReasonTypes.CHAT_UPDATES;
  eventPayload: ChatsUpdates;
}

export enum EntityTypes {
  CHAT = 'CHAT',
  CUSTOMER = 'CUSTOMER',
}

export interface CustomFieldUpdatePayload {
  customerId: string;
  event: string;
  id: string;
  name: string;
  type: string;
  value?: string;
  customFieldValueId?: string;
  values?: Array<{ customFieldValueId: string; value: string }>;
  entityType?: EntityTypes;
  chatId?: string;
}

export type ChatFiltersEventPayload =
  | NewMessagePayload
  | IsDonePayload
  | MarkReadPayload
  | ChatSlaStatusPayload
  | AssignTagsPayload
  | AssigneeUpdatesPayload
  | ChatsUpdatesPayload
  | AssigneeUpdatesBulkPayload
  | WaitingForResponsePayload;

export interface ChatSearchArgs {
  searchTerm?: string;
}

export enum CallSummaryEventReasonTypes {
  SUMMARY = 'summary',
  TRANSCRIPT = 'transcription',
  BULK_SUMMARY = 'bulk_summary',
}

export interface CallSummaryResponsePayload {
  eventReason: CallSummaryEventReasonTypes.SUMMARY;
  eventPayload: {
    orgId: string;
    chatId: string;
    chatMessageId: string;
    quickRecap: string;
  };
}

export interface CallTranscriptResponsePayload {
  eventReason: CallSummaryEventReasonTypes.TRANSCRIPT;
  eventPayload: {
    orgId: string;
    chatId: string;
    chatMessageId: string;
    preview: string;
  };
}

export interface CallBulkSummaryProgressPayload {
  eventReason: CallSummaryEventReasonTypes.BULK_SUMMARY;
  eventPayload: {
    chatId: string;
    orgId: string;
    chatSummaryWeekId?: string;
    progress: number;
    status: CallSummaryProgressStatus;
    state: CallSummaryProgressState;
    processedCount: number;
    totalCalls: number;
  };
}

export type AICallEventPayload =
  | CallSummaryResponsePayload
  | CallTranscriptResponsePayload
  | CallBulkSummaryProgressPayload;
